/**
 * Delivery is offered only when the vehicle says so explicitly. A missing
 * flag (vehicle still loading, failed to load, older payload) counts as
 * not offered: the safe default is pickup.
 */
export function isDeliveryOffered(
  vehicle: { delivery_available?: boolean | null } | null | undefined,
): boolean {
  return vehicle?.delivery_available === true;
}

/**
 * Typed (custom) delivery addresses are offered when the vehicle offers
 * delivery and does not say otherwise. Only an explicit `false` restricts a
 * vehicle to its assigned locations: a payload without the field comes from a
 * server that predates it and still prices typed addresses (the web can ship
 * before the server). The server column is NOT NULL, so null never arrives;
 * it is treated like absent.
 */
export function isCustomAddressOffered(
  vehicle:
    | {
        delivery_available?: boolean | null;
        delivery_custom_address?: boolean | null;
      }
    | null
    | undefined,
): boolean {
  return (
    isDeliveryOffered(vehicle) && vehicle?.delivery_custom_address !== false
  );
}

/**
 * - "none": pickup only
 * - "locations": only the vehicle's assigned delivery locations
 * - "both": assigned locations and typed addresses
 *
 * A vehicle that offers delivery but not typed addresses and has no assigned
 * location has nowhere to deliver to, so it is "none".
 */
export type DeliveryMode = "none" | "locations" | "both";

export function resolveDeliveryMode(
  vehicle:
    | {
        delivery_available?: boolean | null;
        delivery_custom_address?: boolean | null;
      }
    | null
    | undefined,
  locations: readonly unknown[] | null | undefined,
): DeliveryMode {
  if (!isDeliveryOffered(vehicle)) return "none";
  if (isCustomAddressOffered(vehicle)) return "both";
  return locations && locations.length > 0 ? "locations" : "none";
}
